import React, { useEffect, useState } from "react";
import config from "config";
import { asyncWithLDProvider } from "launchdarkly-react-client-sdk";
import { useOktaAuth } from "@okta/okta-react";

import { getTenantFromHostname } from "shared/utils";

import { getTrackingEmailFromIDToken } from "features/auth/Login/utils";

import { oktaAuth } from "services/okta";

const displayUntilReady: React.ReactNode = null;

interface LaunchDarklyWrapperProps {
  children: React.ReactNode;
}

const LaunchDarklyWrapper = ({ children }: LaunchDarklyWrapperProps) => {
  const { authState } = useOktaAuth();

  const isLoginRedirect = oktaAuth.isLoginRedirect();

  // Allow login/callback routes to proceed
  if (isLoginRedirect) {
    return <>{children}</>;
  }

  // Wait for auth state to be determined
  if (!authState) {
    return displayUntilReady;
  }

  // Unauthenticated users get redirected by SecureComponent, but handle edge cases
  if (!authState.isAuthenticated || !authState.idToken) {
    return <>{children}</>;
  }

  // Initialize LaunchDarkly for authenticated users
  const email = getTrackingEmailFromIDToken(authState.idToken);
  if (!email) {
    return <>{children}</>;
  }

  const {
    featureFlags: { launchDarklyClientId },
  } = config.get();

  return (
    <LaunchDarklyProvider
      clientSideID={launchDarklyClientId}
      userKey={email}
      userEmail={email}
    >
      {children}
    </LaunchDarklyProvider>
  );
};

interface LaunchDarklyProviderProps {
  children: React.ReactNode;
  clientSideID: string;
  userKey: string;
  userEmail: string;
}

const LaunchDarklyProvider = ({
  children,
  clientSideID,
  userKey,
  userEmail,
}: LaunchDarklyProviderProps) => {
  const [LDProvider, setLDProvider] = useState<React.ComponentType<{
    children: React.ReactNode;
  }> | null>(null);

  useEffect(() => {
    const initializeLaunchDarkly = async () => {
      try {
        const Provider = await asyncWithLDProvider({
          clientSideID,
          context: {
            kind: "user",
            key: userKey,
            email: userEmail,
            tenant: getTenantFromHostname(),
          },
          timeout: 3,
        });

        setLDProvider(() => Provider);
      } catch (error) {
        console.error("Failed to initialize LaunchDarkly:", error);
        setLDProvider(() => ({ children }: { children: React.ReactNode }) => (
          <>{children}</>
        ));
      }
    };

    initializeLaunchDarkly();
  }, [clientSideID, userKey, userEmail]);

  if (!LDProvider) {
    return displayUntilReady;
  }

  return <LDProvider>{children}</LDProvider>;
};

export default LaunchDarklyWrapper;
